import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";

function GetRateCard() {
  const [rateCards, setRateCards] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [descriptions, setDescriptions] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedDescription, setSelectedDescription] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newServiceCharge, setNewServiceCharge] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRateCards, setTotalRateCards] = useState(0);
  const limit = 50;
  const navigate = useNavigate();

  // Fetch rate cards with backend pagination
  const fetchRateCardData = () => {
    setLoading(true);
    
    const queryParams = new URLSearchParams({
      page,
      limit,
    });

    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
    };
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    fetch(`https://visitguru.in/api/v1/ratecard/findrate?${queryParams}`, {
      headers,
      timeout: 30000
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Network response was not ok: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          console.log("API Response:", data);
          setRateCards(data.data || []);
          setTotalRateCards(data.totalRateCards || data.data?.length || 0);
          setTotalPages(data.pagination?.totalPages || Math.ceil((data.totalRateCards || data.data?.length || 0) / limit) || 1);
          
          const extractedVendors = [
            ...new Set(data.data?.map((card) => card.shopId?.shopName) || []),
          ].filter(Boolean);
          setVendors(extractedVendors);
          
          const extractedCategories = [
            ...new Set(
              (data.data || [])
                .map((card) => card.category?.categoryName)
                .filter((name) => name)
            ),
          ];
          setCategories(extractedCategories);
        } else {
          toast.error("Failed to fetch rate cards");
          setRateCards([]);
          setTotalRateCards(0);
          setTotalPages(1);
        }
      })
      .catch((err) => {
        console.error("Error fetching rate cards:", err);
        toast.error("Failed to fetch rate cards: " + err.message);
        setRateCards([]);
        setTotalRateCards(0);
        setTotalPages(1);
      })
      .finally(() => setLoading(false));
  };

  // Fetch data when page changes
  useEffect(() => {
    fetchRateCardData();
  }, [page]);

  // Update descriptions when selectedCategory or rateCards change
  useEffect(() => {
    if (selectedCategory) {
      const filteredDescriptions = [
        ...new Set(
          rateCards
            .filter((card) => card.category?.categoryName === selectedCategory)
            .map((card) => card.Description)
            .filter((desc) => desc)
        ),
      ];
      setDescriptions(filteredDescriptions);
      if (!filteredDescriptions.includes(selectedDescription)) {
        setSelectedDescription("");
        setSelectedIds([]);
        setNewDescription("");
        setNewServiceCharge("");
      }
    } else {
      const allDescriptions = [
        ...new Set(
          rateCards
            .map((card) => card.Description)
            .filter((desc) => desc)
        ),
      ];
      setDescriptions(allDescriptions);
    }
  }, [selectedCategory, rateCards, selectedDescription]);

  const handleDelete = async (rateCardId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#11ADA3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        "https://visitguru.in/api/v1/ratecard/deleteRateCard",
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ _id: rateCardId }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Server responded with status ${res.status}: ${text}`);
      }

      const data = await res.json();

      if (data.success) {
        toast.success("Rate card deleted successfully!");
        setRateCards((prev) => prev.filter((r) => r._id !== rateCardId));
        setSelectedIds((prev) => prev.filter((id) => id !== rateCardId));
      } else {
        toast.error(data.message || "Failed to delete rate card.");
      }
    } catch (err) {
      console.error("Error deleting rate card:", err);
      toast.error(`Error: ${err.message}`);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("Please select at least one rate card to delete.");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${selectedIds.length} rate card(s). This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#11ADA3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete them!",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const deletePromises = selectedIds.map((rateCardId) =>
          fetch("https://visitguru.in/api/v1/ratecard/deleteRateCard", {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ _id: rateCardId }),
          }).then(async (response) => {
            if (!response.ok) {
              const text = await response.text();
              throw new Error(`Server responded with status ${response.status}: ${text}`);
            }
            return response.json();
          })
        );

        const results = await Promise.all(deletePromises);

        let successCount = 0;
        let errorMessages = [];

        results.forEach((data, index) => {
          if (data.success) {
            successCount++;
            setRateCards((prev) =>
              prev.filter((rateCard) => rateCard._id !== selectedIds[index])
            );
          } else {
            errorMessages.push(
              data.message || `Failed to delete rate card ${selectedIds[index]}.`
            );
          }
        });

        setSelectedIds([]);
        setSelectedDescription("");

        if (successCount === selectedIds.length) {
          toast.success(`${successCount} rate card(s) deleted successfully!`);
        } else if (successCount > 0) {
          toast.success(`${successCount} rate card(s) deleted successfully!`);
          if (errorMessages.length > 0) {
            toast.error(
              `Some rate cards could not be deleted: ${errorMessages.join(", ")}`
            );
          }
        } else {
          toast.error(
            `Failed to delete rate cards: ${errorMessages.join(", ")}`
          );
        }
      } catch (error) {
        console.error("Error deleting selected rate cards:", error);
        toast.error(`Error: ${error.message}`);
      }
    }
  };

  const handleSelectRateCard = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredRateCards.map((rateCard) => rateCard._id));
    } else {
      setSelectedIds([]);
      setSelectedDescription("");
    }
  };

  const handlePrev = () => {
    if (page > 1) {
      setLoading(true);
      setTimeout(() => {
        setPage(page - 1);
        setLoading(false);
      }, 4000);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      setLoading(true);
      setTimeout(() => {
        setPage(page + 1);
        setLoading(false);
      }, 4000);
    }
  };

  const handleSelectDescription = (e) => {
    const desc = e.target.value;
    setSelectedDescription(desc);
    if (desc) {
      const matchingRateCards = rateCards.filter(
        (card) => card.Description === desc
      );
      setNewDescription(desc);
      setNewServiceCharge(matchingRateCards[0]?.ServiceCharge || "");
    } else {
      setNewDescription("");
      setNewServiceCharge("");
    }
  };

  const handleBulkUpdate = async () => {
    if (!selectedDescription || (!newDescription && !newServiceCharge)) {
      toast.error("Please select a description and provide at least one input.");
      return;
    }

    const rateCardIds = rateCards
      .filter((card) => card.Description === selectedDescription)
      .map((card) => card._id);

    if (rateCardIds.length === 0) {
      toast.error("No rate cards selected for update.");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to update ${rateCardIds.length} rate card(s) with description "${selectedDescription}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#11ADA3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update them!",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          "https://visitguru.in/api/v1/ratecard/bulkupdate",
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              rateCardIds,
              Description: newDescription || undefined,
              ServiceCharge: newServiceCharge ? parseFloat(newServiceCharge) : undefined,
            }),
          }
        );

        const data = await response.json();
        if (data.success) {
          toast.success(data.message || "Rate cards updated successfully!");
          setRateCards((prev) =>
            prev.map((card) =>
              rateCardIds.includes(card._id)
                ? {
                    ...card,
                    Description: newDescription || card.Description,
                    ServiceCharge: newServiceCharge ? parseFloat(newServiceCharge) : card.ServiceCharge,
                  }
                : card
            )
          );
          setSelectedDescription("");
          setNewDescription("");
          setNewServiceCharge("");
        } else {
          toast.error(data.message || "Failed to update rate cards");
        }
      } catch (err) {
        console.error("Error updating rate cards:", err);
        toast.error("Failed to update rate cards: " + err.message);
      }
    }
  };

  // Client-side filtering
  const filteredRateCards = rateCards
    .filter((card) =>
      selectedVendor ? card.shopId?.shopName === selectedVendor : true
    )
    .filter((card) =>
      selectedCategory ? card.category?.categoryName === selectedCategory : true
    );

  const generatePDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    doc.setFontSize(14);
    doc.text("Rate Card Records", 14, 15);

    const tableData = filteredRateCards.map((card, index) => [
      index + 1,
      card.shopId?.shopName || "N/A",
      card.category?.categoryName || "N/A",
      card.Description || "N/A",
      card.ServiceCharge || "N/A",
    ]);

    doc.autoTable({
      startY: 20,
      head: [["#", "Shop Name", "Category", "Description", "Service Charge"]],
      body: tableData,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [8, 132, 196], textColor: [255, 255, 255] },
    });

    doc.save("rate_cards.pdf");
  };

  return (
    <div className="space-y-12">
      <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
        <div className="rounded-t px-4 py-3 border-0">
          <h3 className="font-semibold text-base text-blueGray-700 mb-2 flex justify-between items-center">
            <span>
              Rate Cards{" "}
              <span className="ml-2 text-sm text-blueGray">
                (Total: {totalRateCards}, Selected: {selectedIds.length})
              </span>
            </span>
            <div className="flex space-x-4">
              <button
                onClick={generatePDF}
                className="bg-[#0884c4] text-white px-4 py-2 rounded hover:bg-blue-600"
                style={{ backgroundColor: "#0884c4" }}
              >
                Download
              </button>
              <button
                onClick={handleDeleteSelected}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Delete
              </button>
              <button
                onClick={handleBulkUpdate}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                style={{ backgroundColor: "#ff4500" }}
              >
                Submit Update
              </button>
            </div>
          </h3>

          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="w-full md:w-1/4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor
              </label>
              <select
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
                className="w-full px-3 py-1.5 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
              >
                <option value="">All Vendors</option>
                {vendors.map((vendor, index) => (
                  <option key={index} value={vendor}>
                    {vendor}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full md:w-1/4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-1.5 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
              >
                <option value="">All Categories</option>
                {categories.map((category, index) => (
                  <option key={index} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full md:w-1/4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <select
                value={selectedDescription}
                onChange={handleSelectDescription}
                className="w-full px-3 py-1.5 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
              >
                <option value="">Select Description</option>
                {descriptions.map((desc, index) => (
                  <option key={index} value={desc}>
                    {desc}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4">
            <span className="text-sm font-medium text-gray-700">Bulk Update by Description:</span>
            <input
              type="text"
              placeholder="New Description (leave blank to keep)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              className="px-3 py-2 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
            />
            <input
              type="number"
              placeholder="New Service Charge (leave blank to keep)"
              value={newServiceCharge}
              onChange={(e) => setNewServiceCharge(e.target.value)}
              className="px-3 py-2 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
            />
          </div>
        </div>

        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                {[
                  "#",
                  "Shop Name",
                  "Category",
                  "Description",
                  "Service Charge",
                  "Action",
                  "",
                ].map((heading, idx) => (
                  <th
                    key={idx}
                    className="px-6 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 py-3 text-xs uppercase font-semibold text-left"
                  >
                    {heading === "" ? (
                      <input
                        type="checkbox"
                        style={{
                          width: "18px",
                          height: "18px",
                          accentColor: "#11ADA3",
                          border: "2px solid #ccc",
                          borderRadius: "6px",
                          boxShadow:
                            "0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)",
                          transform: "scale(1)",
                          transition: "all 0.2s ease-in-out",
                          cursor: "pointer",
                        }}
                        checked={
                          filteredRateCards.length > 0 &&
                          filteredRateCards.every((card) =>
                            selectedIds.includes(card._id)
                          )
                        }
                        onChange={handleSelectAll}
                        onMouseOver={(e) =>
                          (e.target.style.transform = "scale(1.1)")
                        }
                        onMouseOut={(e) =>
                          (e.target.style.transform = "scale(1)")
                        }
                      />
                    ) : (
                      heading
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-lg text-blueGray-600">
                    Loading...
                  </td>
                </tr>
              ) : (
                filteredRateCards.map((card, index) => (
                  <tr key={card._id}>
                    <td className="border-t-0 px-6 text-xs p-4 text-left">
                      {(page - 1) * limit + index + 1}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      {card.shopId?.shopName || "N/A"}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      {card.category?.categoryName || "N/A"}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      {card.Description || "N/A"}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      ₹{card.ServiceCharge || "N/A"}
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() =>
                            navigate(`/admin/editratecard/${card._id}`, {
                              state: card,
                            })
                          }
                          className="p-2 rounded-full hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-200 transition"
                          aria-label="Edit rate card"
                        >
                          <i className="fas fa-edit text-green-500 text-lg"></i>
                        </button>
                      </div>
                    </td>
                    <td className="border-t-0 px-6 text-xs p-4">
                      <input
                        type="checkbox"
                        style={{
                          width: "18px",
                          height: "18px",
                          accentColor: "#11ADA3",
                          border: "2px solid #ccc",
                          borderRadius: "6px",
                          boxShadow:
                            "0 2px 4px rgba(0,0,0,0.3), inset 0 -2px 4px rgba(0,0,0,0.2)",
                          transform: "scale(1)",
                          transition: "all 0.2s ease-in-out",
                          cursor: "pointer",
                        }}
                        checked={selectedIds.includes(card._id)}
                        onChange={() => handleSelectRateCard(card._id)}
                        onMouseOver={(e) => (e.target.style.transform = "scale(1.1)")}
                        onMouseOut={(e) => (e.target.style.transform = "scale(1)")}
                      />
                    </td>
                  </tr>
                ))
              )}
              {!loading && filteredRateCards.length === 0 && (
                <tr>
                  <td
                    colSpan="7"
                    className="text-center p-4 text-sm text-gray-500"
                  >
                    No rate cards found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center p-4">
          <button
            onClick={handlePrev}
            disabled={page === 1 || loading}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {page} of {totalPages} ({totalRateCards} total rate cards)
          </span>
          <button
            onClick={handleNext}
            disabled={page === totalPages || loading}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default GetRateCard;